import React, { useState } from 'react';

import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { useBaseTable } from 'hooks/useBaseTable';

import { PeriodItemEntry } from 'api/queries/getSummaryTrackedActivities.query';

import { CustomizedStatusBar } from 'components/common/CustomizedStatusBar/CustomizedStatusBar';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import NoData from 'components/common/NoData/noData';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { TitleWithCounter } from 'components/common/titleWithCounter/titleWithCounter';

import {
    IHashmap,
    ITableWidgetWrapperProps,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import tableClasses from 'components/Dashboards/Project/Components/VersionCompare/style/Table.module.scss';
import { projectConfig } from 'components/Dashboards/Project/ProjectWrapper/projectConfig';
import { CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import { columnsV8, initialState } from 'components/widgets/myTrackedActivities/myTrackedActivities.utils';
import { useMyTrackedActivitiesWidgetLogic } from 'components/widgets/myTrackedActivities/myTrackedActivitiesWidget.logic';
import classes from 'components/widgets/myTrackedActivities/myTrackedActivitiesWidget.module.scss';
import { useMyTrackedActivitiesWidgetProvider } from 'components/widgets/myTrackedActivities/myTrackedActivitiesWidget.provider';

const title = 'My Tracked Activities';
const id = 'summary-tracked-activities';
const componentKey = 'myTrackedActivities';

export const MyTrackedActivitiesWidget = ({
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
}: ITableWidgetWrapperProps) => {
    const { table } = useBaseTable({
        widgetId: null,
        localFilters: [],
        componentKey,
        initialState,
        columns: columnsV8,
    });

    const { api, ui } = useMyTrackedActivitiesWidgetProvider({
        projectId,
        contractId,
        latestVersionId,
        compareVersionId,
    });

    const { logic } = useMyTrackedActivitiesWidgetLogic({
        id,
        api,
        ui,
    });

    return (
        <WidgetWithTitle
            title={
                <div className={classes.statusBar}>
                    <TitleWithCounter title={title} counter={logic.activeData?.length} />
                    <div className={classes.statusBarWrapper}>
                        <CustomizedStatusBar data={logic.statusBarData} />
                    </div>
                </div>
            }
            tooltip={api.getSummaryTrackedActivities.data?.info}
            id={id}
            titleComponents={[
                <CustomizedTooltip
                    key={'tableColumns'}
                    tooltipPosition={TooltipPosition.Top}
                    tooltipContent={'Customize table columns'}
                    triggerElement={
                        <CustomizedRoundIcon enableHover={true} onClick={() => table.setModalColumnsOpen(true)}>
                            <Icon name={'tune-filter'} />
                        </CustomizedRoundIcon>
                    }
                />,
                <ShareInsightButton title={title} link={logic.route} />,
                <ExportDropdown title={title} key={'export'} subTitle={logic.subTitle} />,
            ]}
        >
            <div className={tableClasses.tableWrapperContainer}>
                {table.updatedInitialState !== undefined && (
                    <TableV8<PeriodItemEntry>
                        maxHeight={500}
                        data={logic.activeData}
                        columns={columnsV8}
                        isLoading={
                            api.getSummaryTrackedActivities.isLoading || api.getSummaryTrackedActivities.isFetching
                        }
                        noData={
                            <NoData>
                                No tracked activities added yet. Find and add via{' '}
                                <Link
                                    to={AppRoutes.buildPath(
                                        AppRoutes.DASHBOARD_PROJECT_PATH,
                                        projectConfig.battlecards.link,
                                    )}
                                >
                                    {projectConfig.battlecards.title}
                                </Link>
                            </NoData>
                        }
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        columnVisibility={table.columnVisibility}
                        setColumnVisibility={table.setColumnVisibility}
                        columnOrder={table.columnOrder}
                        setColumnOrder={table.setColumnOrder}
                        manualPagination={false}
                        manualSorting={false}
                        rowSelection={table.rowSelection}
                        setRowSelection={table.setRowSelection}
                    />
                )}
            </div>

            <CustomTableColumnsModal
                onHideModal={() => table.setModalColumnsOpen(false)}
                showModal={table.modalColumnsOpen}
                componentKey={componentKey as keyof IHashmap}
                tableInstance={table.tableInstance}
                initialState={initialState}
                columns={columnsV8}
            />
        </WidgetWithTitle>
    );
};
